import {
  ConditionalWrap,
  Grid,
  isFilterEnabled,
  PromptResultsAnnouncement,
  PromptTab,
  PromptTabs,
  ShareToWorkgroupDialog,
  useHandleKeyDown,
  WorkgroupPublisherTeachingPopover,
  type Prompt,
} from "@1js/pl-card-components";
import { mergeClasses, Toaster } from "@fluentui/react-components";
import * as React from "react";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import { PropertyContext } from "../../pages/PropertyContext.web";
import { sendDiagnosticEvent } from "../../utils/Telemetry";
import { LogCategory, LogType } from "../../utils/Telemetry/events";
import { getPromptGalleryAppParameters, navigateToApp } from "../../utils/url";
import { SharedPromptsDialogContainer } from "../ManageSharedPrompts/SharedPromptsDialogContainer.web";
import { YourPrompts } from "../YourPrompts/YourPrompts.web";
import { AgentPrompts } from "./AgentPrompts";
import { useAllPrompts } from "./AllPrompts.hook.web";
import { type AllPromptsProps } from "./AllPrompts.web.data";
import { useStyles } from "./AllPrompts.web.styles";
import { BookmarkPrompts } from "./BookmarkPrompts.web";
import { SuggestedPrompts } from "./SuggestedPrompts.web";
import { WorkgroupPrompts } from "./WorkgroupPrompts.web";
export const AllPrompts: React.FC<AllPromptsProps> = React.memo(
  ({
    id,
    role,
    className,
    skeletonCount,
    status,
    isShowFilters = true,
    isShowHasMore = true,
    enableCarousel = false,
    hasMore,
    hasBookmarkedPrompts,
    prompts,
    allPrompts,
    morePrompts,
    filters,
    workgroupFilters,
    selectedFacets,
    selectedWorkgroupFacets,
    selectedTab,
    onFilterChanged,
    onPromptClicked,
    onPromptBookmarkToggled,
    onMoreClicked,
    onSharePromptClicked,
    onCopyPromptClicked,
    onBookmarkToggled,
    onPromptsTabSelected,
    onGroupPromptsFilterChanged,
    onDelete,
    onTryInAppBadgeAction,
    onShareToWorkgroup,
    groupPrompts,
    onAnnouncementSharePromptsClicked,
    onAnnouncementExploreOtherPromptsClicked,
    groupPromptStatus,
    handlePromptsTabSelected,
    notify,
    isTeachingExperiencesEnabled,
    onRetrieveNotificationGroupMemberCount,
    isMetaOsV2,
    searchResultsData,
    isHeroCard,
    disabledFilters,
    isMetaOsV2HomePage,
    onCollectionTileClick,
    selectedFilterValue,
  }) => {
    const styles = useStyles();
    const {
      activePrompt,
      currentFocusedPromptId,
      dialogOpen,
      handleCardClick,
      handleCardFocus,
      handleDialogClose,
      handleFilterChange,
      handleFiltersCleared,
      handleFiltersOpenChanged,
      handleGroupPromptsFilterOnOpenChange,
      handleShareViaCopyLinkClicked,
      handleShareViaEmailClicked,
      handleTabChange,
      isFlightActive,
      isLoading,
      isShareToWorkgroupDialogOpen,
      isWorkgroupPublishingEnabled,
      onCloseShareToWorkgroupDialog,
      promptRefs,
      promptsToDisplay,
      promptsToShareToWorkgroup,
      resultsAnnouncementId,
      setDialogOpen,
      setIsPopoverOpen,
      setLastFocusedPromptId,
      setCurrentFocusedPromptId,
      setworkgroupPublisherTeachingPopoverTarget,
      shareToWorkgroup,
      swipe,
      toasterId,
      workgroupPublisherTeachingPopoverTarget,
      onGroupDropdownOptionSelect,
      onGroupDropdownOpenChange,
    } = useAllPrompts({
      filters,
      groupPrompts,
      onFilterChanged,
      onPromptClicked,
      onPromptsTabSelected,
      prompts,
      allPrompts,
      status,
      selectedTab,
      onRetrieveNotificationGroupMemberCount,
    });

    const onGoToCopilotLabClicked = () => {
      sendDiagnosticEvent(
        LogType.Informational,
        `navigate to app from teams channel`,
        LogCategory.NAVIGATETOAPP
      );
      navigateToApp(getPromptGalleryAppParameters());
    };

    const [promptSettings] = React.useContext(PropertyContext);
    const { state } = React.useContext(LibraryContext);
    const { groups, groupHasPromptsStatus } = state;
    const isTeamsTab = localStorage.getItem("IsTeamsTab");
    const { createHandleKeyDown } = useHandleKeyDown();

    const handleKeyDown = createHandleKeyDown({
      displayedPrompts: state.displayedPrompts,
      onPressEnterOrSpace: (prompt) => {
        if (handleCardClick) {
          handleCardClick(prompt);
        }
      },
    });

    const handleDeleteBookmarkedPrompt = (prompt: Prompt) => {
      const promptId = prompt.Id;
      const currentCard = promptRefs.current.get(promptId);
      const parentElement = currentCard?.current?.parentElement;
      const nextCard = parentElement?.nextElementSibling?.firstElementChild;
      const previousCard =
        parentElement?.previousElementSibling?.firstElementChild;

      if (onDelete) {
        onDelete(prompt);
      }

      if (nextCard) {
        (nextCard as HTMLElement).focus();
        (nextCard as HTMLElement).tabIndex = 0;
      } else if (previousCard) {
        (previousCard as HTMLElement).focus();
        (previousCard as HTMLElement).tabIndex = 0;
      }
    };

    if (isTeamsTab === "true") {
      isShowFilters = false;
    }
    filters = filters.filter((filter) =>
      isFilterEnabled(
        filter.key,
        selectedTab,
        isFlightActive("isAgentPromptSavingEnabled") ||
          isFlightActive("isAgentPromptFavoriteEnabled"),
        isMetaOsV2
      )
    );
    const announcements = (
      <PromptResultsAnnouncement
        id={resultsAnnouncementId}
        prompts={promptsToDisplay}
        isLoading={
          isLoading ||
          groupPromptStatus === "loading" ||
          groupHasPromptsStatus === "loading"
        }
        isFailed={
          status === "failed" ||
          groupPromptStatus === "failed" ||
          groupHasPromptsStatus === "failed"
        }
        hasBookmarkedPrompts={hasBookmarkedPrompts}
        groups={groups}
        selectedTab={selectedTab}
        groupPrompts={
          isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")
            ? promptsToDisplay
            : groupPrompts
        }
        onExploreOtherPrompts={onAnnouncementExploreOtherPromptsClicked}
        onSharePrompts={onAnnouncementSharePromptsClicked}
        onGoToCopilotLabClicked={onGoToCopilotLabClicked}
      />
    );

    const isMemberOfAnyGroup = groups && groups.length > 0;

    return (
      <>
        <WorkgroupPublisherTeachingPopover
          isEnabled={
            (isFlightActive("isWorkgroupTeachingPopoverEnabled") &&
              !isFlightActive("isGoBoldPromptCardEnabled") &&
              isMemberOfAnyGroup &&
              workgroupPublisherTeachingPopoverTarget &&
              selectedTab === PromptTabs.AllPrompts &&
              isTeachingExperiencesEnabled) ||
            false
          }
          onPopoverStatus={setIsPopoverOpen}
          target={workgroupPublisherTeachingPopoverTarget}
        />
        <Toaster toasterId={toasterId} pauseOnHover pauseOnWindowBlur />

        <ShareToWorkgroupDialog
          isOpen={isShareToWorkgroupDialogOpen}
          groups={groups}
          prompts={promptsToShareToWorkgroup}
          onClose={onCloseShareToWorkgroupDialog}
          onShare={onShareToWorkgroup}
          appearance={"web"}
          isAllowMultiSelect={isFlightActive(
            "isWorkgroupPublishingMultiSelectShareEnabled"
          )}
          onGroupDropdownOptionSelect={onGroupDropdownOptionSelect}
          onGroupDropdownOpenChange={onGroupDropdownOpenChange}
          isNotificationEnabled={isFlightActive(
            "isWorkgroupNotificationEnabled"
          )}
          notificationMemberCounts={state.workgroupNotificationMemberCounts}
          isFetchingGroupMembers={state.groupMemberStatus === "loading"}
        />

        <SharedPromptsDialogContainer />

        <ConditionalWrap
          condition={!enableCarousel}
          wrapIfTrue={(children) => (
            <Grid
              id={id}
              role={role}
              testid="all-prompts"
              className={mergeClasses(
                styles.container,
                isFlightActive("isGoBoldPromptCardEnabled") &&
                  styles.increasedRowGap,
                className
              )}
              goBold={isFlightActive("isGoBoldPromptCardEnabled")}
              isMetaOsV2={isMetaOsV2}
            >
              {children}
            </Grid>
          )}
        >
          {promptSettings.user &&
            isShowFilters &&
            !isMetaOsV2 &&
            (isFlightActive("isPromptSavingEnabled") ||
              (isWorkgroupPublishingEnabled &&
                (isMemberOfAnyGroup || !groups))) && (
              <div className={styles.promptTabRow}>
                <PromptTab
                  loading={isLoading}
                  selectedValue={selectedTab}
                  onTabSelected={handleTabChange}
                  isShowGroupTab={
                    isWorkgroupPublishingEnabled &&
                    (isMemberOfAnyGroup || !groups)
                  }
                  appearance={
                    isFlightActive("isGoBoldPromptCardEnabled")
                      ? "web-v2"
                      : "web"
                  }
                />
              </div>
            )}
          {selectedTab === PromptTabs.WorkplacePrompts && (
            <SuggestedPrompts
              isLoading={isLoading}
              groupPromptStatus={groupPromptStatus}
              currentFocusedPromptId={currentFocusedPromptId}
              enableCarousel={enableCarousel}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              isMetaOsV2={isMetaOsV2}
              searchResultsData={searchResultsData}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              onFilterChanged={onFilterChanged}
              setworkgroupPublisherTeachingPopoverTarget={
                setworkgroupPublisherTeachingPopoverTarget
              }
              handlePromptsTabSelected={handlePromptsTabSelected}
              handleKeyDown={handleKeyDown}
              isHeroCard={isHeroCard}
              disabledFilters={disabledFilters}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
              selectedTab={selectedTab}
            >
              <></>
            </SuggestedPrompts>
          )}
          {(selectedTab === PromptTabs.MicrosoftPrompts ||
            selectedTab === PromptTabs.CombinedPrompts) && (
            <SuggestedPrompts
              isLoading={isLoading}
              groupPromptStatus={groupPromptStatus}
              currentFocusedPromptId={currentFocusedPromptId}
              enableCarousel={enableCarousel}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              isMetaOsV2={isMetaOsV2}
              searchResultsData={searchResultsData}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              onFilterChanged={onFilterChanged}
              setworkgroupPublisherTeachingPopoverTarget={
                setworkgroupPublisherTeachingPopoverTarget
              }
              handlePromptsTabSelected={handlePromptsTabSelected}
              handleKeyDown={handleKeyDown}
              isHeroCard={isHeroCard}
              disabledFilters={disabledFilters}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
              selectedTab={selectedTab}
            >
              <></>
            </SuggestedPrompts>
          )}
          {selectedTab === PromptTabs.BrowsePrompts && (
            <SuggestedPrompts
              isLoading={isLoading}
              groupPromptStatus={groupPromptStatus}
              currentFocusedPromptId={currentFocusedPromptId}
              enableCarousel={enableCarousel}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              isMetaOsV2={isMetaOsV2}
              searchResultsData={searchResultsData}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              onFilterChanged={onFilterChanged}
              setworkgroupPublisherTeachingPopoverTarget={
                setworkgroupPublisherTeachingPopoverTarget
              }
              handlePromptsTabSelected={handlePromptsTabSelected}
              handleKeyDown={handleKeyDown}
              isHeroCard={isHeroCard}
              disabledFilters={disabledFilters}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
              selectedTab={selectedTab}
              selectedFilterValue={selectedFilterValue}
            >
              <></>
            </SuggestedPrompts>
          )}
          {selectedTab === PromptTabs.AllPrompts && (
            <SuggestedPrompts
              isLoading={isLoading}
              groupPromptStatus={groupPromptStatus}
              currentFocusedPromptId={currentFocusedPromptId}
              enableCarousel={enableCarousel}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              isMetaOsV2={isMetaOsV2}
              searchResultsData={searchResultsData}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              onFilterChanged={onFilterChanged}
              setworkgroupPublisherTeachingPopoverTarget={
                setworkgroupPublisherTeachingPopoverTarget
              }
              handlePromptsTabSelected={handlePromptsTabSelected}
              handleKeyDown={handleKeyDown}
              isHeroCard={isHeroCard}
              disabledFilters={disabledFilters}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
              selectedFilterValue={selectedFilterValue}
            >
              {announcements}
            </SuggestedPrompts>
          )}
          {selectedTab === PromptTabs.SearchPrompts && ( //TODO: For time being use SuggestedPrompts for search prompts
            <SuggestedPrompts
              isLoading={isLoading}
              groupPromptStatus={groupPromptStatus}
              currentFocusedPromptId={currentFocusedPromptId}
              enableCarousel={enableCarousel}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              prompts={prompts}
              groups={groups}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              morePrompts={morePrompts}
              isMetaOsV2={isMetaOsV2}
              searchResultsData={searchResultsData}
              onSharePromptClicked={onSharePromptClicked}
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onBookmarkToggled={onBookmarkToggled}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              onFilterChanged={onFilterChanged}
              setworkgroupPublisherTeachingPopoverTarget={
                setworkgroupPublisherTeachingPopoverTarget
              }
              handlePromptsTabSelected={handlePromptsTabSelected}
              handleKeyDown={handleKeyDown}
              isHeroCard={isHeroCard}
              disabledFilters={disabledFilters}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
              onCollectionTileClick={onCollectionTileClick}
            >
              <></>
            </SuggestedPrompts>
          )}
          {(selectedTab === PromptTabs.SavedPrompts ||
            selectedTab === PromptTabs.LikedPrompts) &&
            (isFlightActive("isPromptLikingEnabled") ? (
              <YourPrompts
                currentFocusedPromptId={currentFocusedPromptId}
                setCurrentFocusedPromptId={setCurrentFocusedPromptId}
                isLoading={isLoading}
                skeletonCount={skeletonCount}
                promptsToDisplay={promptsToDisplay}
                promptRefs={promptRefs}
                shareToWorkgroup={shareToWorkgroup}
                handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
                handleShareViaEmailClicked={handleShareViaEmailClicked}
                handleCardClick={handleCardClick}
                onPromptBookmarkToggled={onPromptBookmarkToggled}
                onDelete={onDelete}
                setLastFocusedPromptId={setLastFocusedPromptId}
                prompts={prompts}
                groups={groups}
                isShowHasMore={isShowHasMore}
                hasMore={hasMore}
                handleCardFocus={handleCardFocus}
                onMoreClicked={onMoreClicked}
                resultsAnnouncementId={resultsAnnouncementId}
                swipe={swipe}
                isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
                dialogOpen={dialogOpen}
                handleDialogClose={handleDialogClose}
                setDialogOpen={setDialogOpen}
                activePrompt={activePrompt}
                morePrompts={morePrompts}
                onSharePromptClicked={onSharePromptClicked}
                onCopyPromptClicked={onCopyPromptClicked}
                onTryInAppBadgeAction={onTryInAppBadgeAction}
                isShowFilters={isShowFilters}
                onBookmarkToggled={onBookmarkToggled}
                filters={filters}
                selectedFacets={selectedFacets}
                handleFilterChange={handleFilterChange}
                onFiltersCleared={handleFiltersCleared}
                onFiltersOpenChanged={handleFiltersOpenChanged}
                handlePromptsTabSelected={handlePromptsTabSelected}
                isMetaOsV2={isMetaOsV2}
                searchResultsData={searchResultsData}
                selectedTab={selectedTab}
                isMetaOsV2HomePage={isMetaOsV2HomePage}
              >
                {announcements}
              </YourPrompts>
            ) : (
              <BookmarkPrompts
                currentFocusedPromptId={currentFocusedPromptId}
                setCurrentFocusedPromptId={setCurrentFocusedPromptId}
                isLoading={isLoading}
                skeletonCount={skeletonCount}
                promptsToDisplay={promptsToDisplay}
                promptRefs={promptRefs}
                shareToWorkgroup={shareToWorkgroup}
                handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
                handleShareViaEmailClicked={handleShareViaEmailClicked}
                handleCardClick={handleCardClick}
                onPromptBookmarkToggled={onPromptBookmarkToggled}
                onDelete={handleDeleteBookmarkedPrompt}
                handleKeyDown={handleKeyDown}
                setLastFocusedPromptId={setLastFocusedPromptId}
                prompts={prompts}
                groups={groups}
                isShowHasMore={isShowHasMore}
                hasMore={hasMore}
                handleCardFocus={handleCardFocus}
                onMoreClicked={onMoreClicked}
                resultsAnnouncementId={resultsAnnouncementId}
                swipe={swipe}
                isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
                dialogOpen={dialogOpen}
                handleDialogClose={handleDialogClose}
                setDialogOpen={setDialogOpen}
                activePrompt={activePrompt}
                morePrompts={morePrompts}
                onSharePromptClicked={onSharePromptClicked}
                onCopyPromptClicked={onCopyPromptClicked}
                onTryInAppBadgeAction={onTryInAppBadgeAction}
                isShowFilters={isShowFilters}
                onBookmarkToggled={onBookmarkToggled}
                filters={filters}
                selectedFacets={selectedFacets}
                handleFilterChange={handleFilterChange}
                onFiltersCleared={handleFiltersCleared}
                onFiltersOpenChanged={handleFiltersOpenChanged}
                handlePromptsTabSelected={handlePromptsTabSelected}
                isMetaOsV2={isMetaOsV2}
                searchResultsData={searchResultsData}
              >
                {announcements}
              </BookmarkPrompts>
            ))}
          {selectedTab === PromptTabs.GroupPrompts && (
            <WorkgroupPrompts
              currentFocusedPromptId={currentFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              isLoading={groupPromptStatus === "loading"}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              handleCardClick={handleCardClick}
              setLastFocusedPromptId={setLastFocusedPromptId}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              groups={groups}
              hasMore={hasMore}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              morePrompts={morePrompts}
              isShowHasMore={
                isFlightActive("isUnifiedWorkgroupPromptsViewEnabled") &&
                isShowHasMore
              }
              onCopyPromptClicked={onCopyPromptClicked}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              onGroupPromptsFilterChanged={onGroupPromptsFilterChanged}
              handleGroupPromptsFilterOnOpenChange={
                handleGroupPromptsFilterOnOpenChange
              }
              filters={
                isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")
                  ? filters
                  : workgroupFilters
              }
              selectedFacets={
                isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")
                  ? selectedFacets
                  : selectedWorkgroupFacets
              }
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              notify={notify}
              handlePromptsTabSelected={handlePromptsTabSelected}
              handleKeyDown={handleKeyDown}
              isMetaOsV2={isMetaOsV2}
              searchResultsData={searchResultsData}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
            >
              {announcements}
            </WorkgroupPrompts>
          )}
          {isMetaOsV2 && selectedTab === PromptTabs.AgentPrompts && (
            <AgentPrompts
              currentFocusedPromptId={currentFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              isLoading={isLoading}
              skeletonCount={skeletonCount}
              promptsToDisplay={promptsToDisplay}
              promptRefs={promptRefs}
              handleCardClick={handleCardClick}
              setLastFocusedPromptId={setLastFocusedPromptId}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              hasMore={hasMore}
              resultsAnnouncementId={resultsAnnouncementId}
              swipe={swipe}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              setDialogOpen={setDialogOpen}
              activePrompt={activePrompt}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              morePrompts={morePrompts}
              isShowHasMore={isShowHasMore}
              isMetaOsV2={isMetaOsV2}
              onCopyPromptClicked={onCopyPromptClicked}
              searchResultsData={searchResultsData}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isShowFilters={isShowFilters}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              onFiltersCleared={handleFiltersCleared}
              onFiltersOpenChanged={handleFiltersOpenChanged}
              onFilterChanged={onFilterChanged}
              handleKeyDown={handleKeyDown}
              isMetaOsV2HomePage={isMetaOsV2HomePage}
            >
              {announcements}
            </AgentPrompts>
          )}
        </ConditionalWrap>
      </>
    );
  }
);

AllPrompts.displayName = "AllPrompts";
